import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Portalized dropdown that avoids clipping and can flip above if space is limited
export default function PortalDropdown({
  anchorEl,
  onClose,
  children,
  align = 'end', // 'start' | 'end'
  offset = 8,
  matchWidth = false,
  autoFlip = true,
  preferDown = true,
}) {
  const menuRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, minWidth: undefined, maxHeight: undefined });

  const isOpen = Boolean(anchorEl);

  const computePosition = () => {
    if (!anchorEl || !menuRef.current) return;
    const anchorRect = anchorEl.getBoundingClientRect();
    const menuEl = menuRef.current;

    // Measure natural menu size
    const prevVis = menuEl.style.visibility;
    const prevTransform = menuEl.style.transform;
    menuEl.style.visibility = 'hidden';
    menuEl.style.transform = 'none';
    const menuWidth = menuEl.offsetWidth;
    const menuHeight = menuEl.offsetHeight;
    menuEl.style.visibility = prevVis;
    menuEl.style.transform = prevTransform;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Horizontal position
    let left = align === 'start' ? anchorRect.left : anchorRect.right - menuWidth;
    left = Math.max(8, Math.min(left, viewportWidth - menuWidth - 8));

    // Decide below vs above
    const spaceBelow = viewportHeight - (anchorRect.bottom + offset) - 8;
    const spaceAbove = anchorRect.top - offset - 8;
    let openBelow = preferDown;
    if (autoFlip) {
      // Flip above if below space is smaller than a practical threshold and above has more room
      const needed = Math.min(menuHeight, 160);
      if (spaceBelow < needed && spaceAbove > spaceBelow) {
        openBelow = false;
      }
    }

    let top;
    let maxHeight;
    if (openBelow) {
      top = anchorRect.bottom + offset;
      maxHeight = Math.max(120, viewportHeight - top - 8);
    } else {
      maxHeight = Math.max(120, spaceAbove);
      const visibleHeight = Math.min(menuHeight, maxHeight);
      top = Math.max(8, anchorRect.top - offset - visibleHeight);
    }

    const minWidth = matchWidth ? Math.max(anchorRect.width, 180) : undefined;
    setPosition({ top, left, minWidth, maxHeight });
  };

  useLayoutEffect(() => {
    if (!isOpen) return;
    computePosition();
    const r = requestAnimationFrame(computePosition);
    return () => cancelAnimationFrame(r);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, anchorEl]);

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = () => computePosition();
    const handleResize = () => computePosition();
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose?.(); };
    const handleDocClick = (e) => {
      if (!menuRef.current || !anchorEl) return;
      const inMenu = menuRef.current.contains(e.target);
      const inAnchor = anchorEl.contains(e.target);
      if (!inMenu && !inAnchor) onClose?.();
    };
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleDocClick, true);
    document.addEventListener('touchstart', handleDocClick, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleDocClick, true);
      document.removeEventListener('touchstart', handleDocClick, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, anchorEl]);

  if (!isOpen) return null;

  return createPortal(
    <div className="portal-dropdown" style={{ position: 'fixed', top: position.top, left: position.left, zIndex: 100000, minWidth: position.minWidth }}>
      <div ref={menuRef} className="portal-dropdown-inner" style={{ maxHeight: position.maxHeight }}>
        {children}
      </div>
    </div>,
    document.body
  );
}


